import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import i18n from '@/i18n';
import { SUPPORTED_CODES } from '@/constants/languages';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

/** Apply the user's preferred_lang to i18next so the whole UI switches.
 *  iter72: supports all 11 locales defined in constants/languages.js.
 *  iter234: gated on i18n.isInitialized to prevent a post-login race where
 *  React was mid-rendering with the old language while i18next was still
 *  loading the new bundle synchronously. The deferred listener still
 *  applies the change once i18next signals 'initialized'. */
function syncUiLang(preferred, fallbackLang) {
  // iter240f — Order of preference: preferred_lang (explicit user choice) →
  // language (legacy field still used by some flows). If both are empty,
  // i18next keeps its detector result (localStorage/browser/htmlTag).
  const candidate = preferred || fallbackLang || '';
  const lang = String(candidate).toLowerCase().slice(0, 2);
  if (!SUPPORTED_CODES.includes(lang)) return;
  if (i18n.language === lang) return;
  if (i18n.isInitialized) {
    i18n.changeLanguage(lang).catch((e) => {
      // eslint-disable-next-line no-console
      console.error('[auth] changeLanguage failed:', e?.message || e);
    });
    return;
  }
  // Defer until i18next finishes its synchronous-then-async init.
  const apply = () => {
    i18n.changeLanguage(lang).catch(() => { /* logged above */ });
    i18n.off('initialized', apply);
  };
  i18n.on('initialized', apply);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // iter171 — Resilient session restore.
    // We MUST distinguish 3 outcomes:
    //   (a) 200 → user is authenticated, set state.
    //   (b) 401/403 → no session, set null (anon path).
    //   (c) 5xx / network blip → DO NOT log the user out. Retry with
    //       short backoff, then keep last known state silently. This
    //       prevents a momentary backend hiccup from kicking a logged-
    //       in user back to /login with a "Service indisponible" toast.
    //
    // iter284 P.B — Skip the retry loop entirely when the browser has
    // no auth cookie: an anonymous 401 is EXPECTED and we shouldn't
    // burn 1.2s of spinner time on it. We check the CSRF cookie as a
    // fast proxy for "have we ever seen this device before?" — if it's
    // absent, we're definitely anonymous and can short-circuit.
    const hasCookieHint = typeof document !== 'undefined' &&
      document.cookie.split(';').some((c) => {
        const t = c.trim();
        return t.startsWith('csrf_token=') || t.startsWith('access_token=')
            || t.startsWith('refresh_token=') || t.startsWith('session_token=');
      });
    if (!hasCookieHint) {
      // Fast anonymous path — no network call, no retry.
      setLoading(false);
      return;
    }
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    const tryOnce = async () => {
      const { data } = await axios.get(`${API}/api/auth/me`, {
        withCredentials: true,
        validateStatus: (s) => s < 500,
      });
      return data;
    };
    let attempts = 0;
    while (attempts < 3) {
      try {
        const data = await tryOnce();
        if (data && data.user_id) {
          setUser(data);
          syncUiLang(data?.preferred_lang, data?.language);
          break;
        }
        // 401/403 path → not authenticated. Stop retrying.
        setUser(null);
        break;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          setUser(null);
          break;
        }
        attempts += 1;
        if (attempts >= 3) break;
        await sleep(400 * attempts);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // iter71: auto-tag OneSignal with our internal user_id whenever a user
  // is resolved. This is idempotent — OneSignal.login() is a no-op when
  // called with the same external id twice. Runs even if the user hasn't
  // opted into push yet, so the moment they do, targeting works.
  // iter273g — Phase 2: also fire a one-shot prefetch of the lazy
  // routes the user is most likely to visit next, in priority order
  // (Feed → Chat → Profile → Admin if role admin). Runs sequentially
  // via requestIdleCallback so it never delays a real interaction.
  useEffect(() => {
    if (!user?.user_id) return;
    (async () => {
      try {
        const { identifyUser } = await import('@/services/webpush');
        await identifyUser(user.user_id);
      } catch (_) {
        // Silent: push tagging failure shouldn't break auth flow.
      }
    })();
    (async () => {
      try {
        // Check the kill switch lazily so a stale cache never blocks
        // user-critical paths. Defaults to ENABLED on any failure.
        let enabled = true;
        try {
          const { data } = await axios.get(`${API}/api/settings/public`, { timeout: 4000 });
          enabled = String(data?.smart_prefetch_enabled ?? 'true').toLowerCase() !== 'false';
        } catch { /* keep default */ }
        const { prefetchPostLogin } = await import('@/utils/prefetchQueue');
        prefetchPostLogin({ role: user?.role, enabled });
      } catch (_) {
        // Silent: prefetch is purely additive.
      }
    })();
  }, [user?.user_id, user?.role]);

  // UET Enhanced Conversions (hashed client-side): follows the current email/phone, never a stale copy.
  const uetEmail = user?.email;
  const uetPhone = user?.phone || user?.phone_number;
  useEffect(() => {
    if (!user?.user_id) return;
    import('@/lib/uet').then(({ setEnhancedConversions }) => setEnhancedConversions({ email: uetEmail, phone: uetPhone })).catch(() => {});
  }, [user?.user_id, uetEmail, uetPhone]);

  const login = useCallback(async (email, password, captchaOrTurnstile) => {
    // iter141ter — `captchaOrTurnstile` can be either:
    //   • { captcha_id, captcha_answer } (new math captcha)
    //   • a string (legacy Turnstile token)
    // The backend accepts both shapes (via Optional fields).
    // IBW persistent-session follow-up — `remember_me` is no longer sent
    // by the SPA (the UI checkbox was removed). The backend policy
    // (`session_persist_default`) decides, which defaults to persistent
    // cookies so users stay logged in across browser closes. The field
    // remains supported server-side as an explicit override for future
    // kiosk-type flows.
    const body = { email, password };
    if (captchaOrTurnstile && typeof captchaOrTurnstile === 'object') {
      body.captcha_id = captchaOrTurnstile.captcha_id;
      body.captcha_answer = captchaOrTurnstile.captcha_answer;
    } else if (typeof captchaOrTurnstile === 'string') {
      body.turnstile_token = captchaOrTurnstile;
    }
    const { data } = await axios.post(`${API}/api/auth/login`, body, {
      withCredentials: true,
      // iter240l-prodfix — hard ceiling so the button cannot stick on
      // "Signing in…" forever if the backend hangs (P0 outage 15/05/26).
      // 15s comfortably absorbs bcrypt + tokens + cookies even on slow
      // Neon cold-starts, and surfaces a clear "timeout" toast otherwise.
      timeout: 15000,
    });
    // Iter83 — superadmin flow returns {status:'otp_required'} instead of
    // {user,...}. Only set the user context when a real user payload is
    // present; otherwise hand the status back to the caller so the UI can
    // switch to the 2FA step.
    if (data?.user) {
      setUser(data.user);
      syncUiLang(data.user?.preferred_lang, data.user?.language);
      // iter274i — UET: First Login (dedup via localStorage per user).
      try {
        const { trackFirstLogin } = await import('../lib/uet');
        trackFirstLogin({ userId: data.user.user_id, isFirstLogin: data.first_login });
      } catch (_e) { /* never break login */ }
    }
    return data;
  }, []);

  const verifySuperadmin2fa = useCallback(async (email, code) => {
    const { data } = await axios.post(
      `${API}/api/auth/verify-2fa`,
      { email, code },
      { withCredentials: true },
    );
    if (data?.user) {
      setUser(data.user);
      syncUiLang(data.user?.preferred_lang, data.user?.language);
    }
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    // iter73 + iter74: auto-detect UI language + wallet currency from the
    // browser and pass them along. `navigator.language` → "fr-CA" → "fr".
    // `Intl.NumberFormat().resolvedOptions().locale` + `Intl.Locale` give
    // us a best-effort currency guess when the browser exposes it.
    // `SUPPORTED_CODES` filters out anything outside our 11-language
    // whitelist so the backend never sees garbage.
    let detected_lang, detected_currency;
    try {
      const nav = (typeof navigator !== 'undefined' && navigator.language) || '';
      const short = nav.toLowerCase().slice(0, 2);
      if (short && SUPPORTED_CODES.includes(short)) {
        detected_lang = short;
      }
    } catch (_) { /* ignore — backend will fall back */ }
    try {
      // Modern browsers expose the user's region via Intl.Locale.
      // Extract region (e.g. "fr-CM" → "CM") and let the backend map it.
      const loc = typeof Intl !== 'undefined' && Intl.DateTimeFormat
        ? Intl.DateTimeFormat().resolvedOptions()
        : null;
      const region = (loc?.locale?.split('-')?.[1] || '').toUpperCase();
      if (region && region.length === 2) {
        // We don't know the currency directly, but payload.country_code
        // will carry the hint. If the user left country blank, we send
        // the browser region as a secondary hint.
        if (!payload.country_code) {
          payload = { ...payload, country_code: region };
        }
      }
    } catch (_) { /* ignore */ }
    const { data } = await axios.post(
      `${API}/api/auth/register`,
      { ...payload, detected_lang, detected_currency },
      { withCredentials: true }
    );
    return data;
  }, []);

  const verifyOtp = useCallback(async (email, code) => {
    const { data } = await axios.post(`${API}/api/auth/verify-otp`, { email, code }, { withCredentials: true });
    setUser(data.user);
    syncUiLang(data.user?.preferred_lang, data.user?.language);
    // iter274i — UET: OTP verification confirms both Complete Registration
    // (backend requires OTP before the account is usable) AND Email
    // Verification. Both events fire ONCE per user via localStorage dedup.
    try {
      const uet = await import('../lib/uet');
      uet.trackCompleteRegistration({ userId: data.user?.user_id, method: 'email' });
      uet.trackEmailVerification({ userId: data.user?.user_id });
      // If this registration was via a referral link, credit the referrer.
      try {
        const refCode = sessionStorage.getItem('japap_ref_code');
        if (refCode) {
          uet.trackReferralRegistrationSuccess({
            userId: data.user?.user_id, referrerCode: refCode,
          });
          sessionStorage.removeItem('japap_ref_code');
        }
      } catch (_e) { /* silent */ }
    } catch (_e) { /* never break verify flow */ }
    return data;
  }, []);

  const resendOtp = useCallback(async (email) => {
    const { data } = await axios.post(`${API}/api/auth/resend-otp`, { email }, { withCredentials: true });
    return data;
  }, []);

  const googleLogin = useCallback(async (sessionId) => {
    const { data } = await axios.post(`${API}/api/auth/google/session`, { session_id: sessionId }, { withCredentials: true });
    setUser(data.user);
    syncUiLang(data.user?.preferred_lang, data.user?.language);
    // iter274i — UET: Google users skip OTP, so we track both Complete
    // Registration (if new) and First Login here. Both helpers dedup
    // internally via localStorage.
    try {
      const uet = await import('../lib/uet');
      if (data?.new_user || data?.first_login) {
        uet.trackCompleteRegistration({ userId: data.user?.user_id, method: 'google' });
        uet.trackEmailVerification({ userId: data.user?.user_id });
      }
      uet.trackFirstLogin({ userId: data.user?.user_id, isFirstLogin: data.first_login });
    } catch (_e) { /* never break login */ }
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch {}
    // iter274l — Clear UET Enhanced Conversions identifiers so the next
    // user session doesn't get attributed to the previous user's hashes.
    try {
      const { clearEnhancedConversions } = await import('@/lib/uet');
      clearEnhancedConversions();
    } catch (_) { /* silent */ }
    // Local JAPAP Pages wizard drafts stay on the server (Mes pages) but not on a shared device.
    try { Object.keys(localStorage).filter((k) => k.startsWith('jpages_wizard_v1:')).forEach((k) => localStorage.removeItem(k)); } catch (_) { /* silent */ }
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {}
  }, []);

  // iter284 — Memoize context value to prevent cascade re-renders of the
  // entire app whenever any local state (or any parent) re-renders.
  const value = useMemo(
    () => ({ user, loading, login, verifySuperadmin2fa, register, verifyOtp, resendOtp, googleLogin, logout, refreshUser, setUser }),
    [user, loading, login, verifySuperadmin2fa, register, verifyOtp, resendOtp, googleLogin, logout, refreshUser],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
